import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import "./css/Otp.css";
import Header from "../components/LandingComponents/Header";
import LandingFooter from "../components/LandingComponents/LandingFooter";
import {
  useVerifyOtpMutation,
  useResendOtpMutation,
} from "../slices/userApiSlice";
import { toast } from "sonner";

const Otppages = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Retrieve email from location state or localStorage
  const storedEmail = localStorage.getItem("email");
  const email = location.state?.email || storedEmail;

  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const otpRefs = useRef([]);
  const [verifyOtp, { isLoading }] = useVerifyOtpMutation();
  const [resendOtp] = useResendOtpMutation();

  // Store email in localStorage when the page loads
  useEffect(() => {
    if (email) {
      localStorage.setItem("email", email);
    } else {
      toast.error(
        "Email Not Found: No email provided. Please check the email link or try again.",
      );
      navigate("/forgotpassword");
    }
  }, [email, navigate]);

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return; // Only allow single-digit numbers

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Store the updated OTP in localStorage
    localStorage.setItem("otp", newOtp.join(""));

    // Move to the next input field only if the current one is filled
    if (value && index < 5) {
      otpRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1].focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const otpString = otp.join("");

    if (otpString.length !== 6) {
      toast.error("Invalid OTP: Please enter the full OTP.");
      return;
    }

    try {
      const response = await verifyOtp({ email, otp: otpString }).unwrap();

      // Store email and OTP again before navigating to ensure they are not lost
      localStorage.setItem("email", email);
      localStorage.setItem("otp", otpString);

      toast.success("OTP Verified: Your OTP is valid.");
      navigate("/newpassword", { state: { email } });
    } catch (error) {
      console.error("OTP Verification Error:", error);
      toast.error(
        error?.data?.message ||
          "OTP Verification Failed: Invalid OTP. Please try again.",
      );
    }
  };

  const handleResendOtp = async () => {
    const toastId = toast.loading(
      "Resending OTP: Please wait while we resend your OTP.",
    );

    try {
      const response = await resendOtp(email).unwrap();

      toast.dismiss(toastId);
      toast.success(
        response?.message ||
          "OTP Resent: A new OTP has been sent to your email.",
      );

      // Clear OTP from localStorage after resending
      localStorage.removeItem("otp");
      setOtp(["", "", "", "", "", ""]);
    } catch (error) {
      toast.dismiss(toastId);
      console.error("Resend OTP Error:", error);
      toast.error(
        error?.data?.message ||
          "Failed to Resend OTP: Could not resend OTP. Try again later.",
      );
    }
  };

  return (
    <div className="login-wrapper">
      <Header />
      <div className="login-main">
        <div className="login-bg-overlay"></div>

        <div className="login-card">
          <div className="login-card-header">
            <h2 className="login-title">Verify OTP</h2>
            <p className="login-subtitle">
              Please enter the 6-digit verification code sent to
              <br />
              <strong>{email}</strong>
            </p>
          </div>

          <form className="login-form" onSubmit={handleSubmit}>
            <div className="otp-box-container">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (otpRefs.current[index] = el)}
                  type="text"
                  maxLength="1"
                  className="otp-box"
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  autoComplete="one-time-code"
                />
              ))}
            </div>

            <button
              type="submit"
              className="login-btn-primary"
              disabled={isLoading}
            >
              {isLoading ? "Verifying..." : "Verify Code"}
            </button>

            <button
              type="button"
              className="login-btn-secondary"
              onClick={handleResendOtp}
            >
              Resend Code
            </button>
          </form>

          <div className="login-card-footer">
            <Link to="/forgotpassword" className="login-back-link">
              <span>Change Email</span>
            </Link>
          </div>
        </div>
      </div>
      <LandingFooter />
    </div>
  );
};

export default Otppages;
